import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import '../../ui/pageScaffolds/SidePanelScaffold/SidePanelScaffold.css';
import '../../ui/Shared.css';
import { IoMdSend } from 'react-icons/io';
import { IoSettingsOutline } from 'react-icons/io5';
import InputField from '../../ui/components/InputField';
import DropDownMenu from '../../ui/components/DropDownMenu';
import NumberInputField from '../../ui/components/NumberInputField';
import SourceCard from '../../ui/components/SourceCard';
import { useNavigate } from 'react-router-dom';
import SidePanelScaffold from '../../ui/pageScaffolds/SidePanelScaffold';
import ChatTopBar from '../../ui/panels/TopBars/ChatTopBar';
import LoadingSpinner from '../../ui/components/LoadingSpinner';
import { LoginState } from '../../types/LoginState';
import BugButton from '../../ui/components/BugButton/BugButton';
import FeedBackButton from '../../ui/components/FeedBackButton/FeedBackButton';
import { Source } from '../../types/Source';
import settingsIcon from '../../images/settings-icon.svg';
import editIcon from '../../images/edit-icon.svg';
import { UserUtils } from '../../utils/UserUtils';
import ChatInputField from '../../ui/components/ChatInputField';
const isMock = process.env.REACT_APP_MOCK === 'true';

// import editIcon from '../images/edit-icon.svg';

interface ChatMessage {
  role: string;
  text: string;
  sources?: Source[];
  showSources?: boolean;
}

interface TrimmedHistory {
  chatHistoryId: number;
  text: string;
}

interface ChatSetting {
  educationLevel: string;
  detailLevel: string;
  threshold: number;
}

interface BotResponse {
  chatText: string;
  sources: Source[];
}

interface ChatData {
  query: string;
  botResponse: BotResponse;
  chatMessages: ChatMessage[];
  chatId: number;
}

function LetterPage() {
  const token = localStorage.getItem('token'); // Retrieve the token
  const nav = useNavigate();
  const levels = ['Short', 'Medium', 'Long'];
  const [hide, setHide] = useState(true);
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  // Get current user name from token
  const currentUserName = UserUtils.getCurrentUserName();

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        localStorage.removeItem('token');
        nav('/');
        return;
      }
      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  useEffect(() => {
    validateUserLogin();
  }, []);

  const [chatSettings, setChatSettings] = useState<ChatSetting>({
    educationLevel: 'A high school student',
    detailLevel: levels[0],
    threshold: 40,
  });

  const [chatData, setChatData] = useState<ChatData>({
    query: '',
    botResponse: { chatText: '', sources: [] },
    chatMessages: [],
    chatId: -1,
  });

  const [ws, setWs] = useState<WebSocket | null>(null);

  const [sideBarExpanded, setSideBarExpanded] = useState<boolean>(true);

  const [recentHistory, setRecentHistory] = useState<TrimmedHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  // TODO need to make a more robust method of selecting entities
  const [selectablePackages, setSelectablePackages] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);

  // const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
  // const handleQueryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
  const handleQueryChange = (event: string) => {
    setChatData((prev) => ({
      ...prev,
      // query: event.target.value,
      query: event,
    }));
  };

  const handleEducationLevelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setChatSettings({
      educationLevel: event.target.value,
      detailLevel: chatSettings.detailLevel,
      threshold: chatSettings.threshold,
    });
  };

  const handleThresholdChange = (value: number) => {
    setChatSettings({
      educationLevel: chatSettings.educationLevel,
      detailLevel: chatSettings.detailLevel,
      threshold: value,
    });
  };

  const handleDetailChange = (value: string) => {
    setChatSettings({
      educationLevel: chatSettings.educationLevel,
      detailLevel: value,
      threshold: chatSettings.threshold,
    });
  };

  const toggleSideBarExpansion = () => {
    setSideBarExpanded(!sideBarExpanded);
  };

  const getValidPackages = async () => {
    const url = '/validpackages';
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }
        throw new Error(`Network response was not ok`);
        // TODO should set a variable here
      }
      const json = await response.json();
      const packages: string[] = json;
      setSelectablePackages(packages);
      setSelectedPackages([packages[0]]); // TODO Critical, when updating with actual selection capability remove this.
    } catch (error) {
      // TODO should set a variable here
    }
  };

  const startWebSocket = (callback?: () => void) => {
    // Close the previous WebSocket if it's already open
    const mockEndpoint = isMock ? '/mock' : '';

    if (ws) {
      ws.close();
      setWs(null);
    }

    // Initialize the WebSocket connection
    let socket: WebSocket;
    if (process.env.REACT_APP_ENV === 'local') {
      socket = new WebSocket(`ws://localhost:3000/admin/letter${mockEndpoint}`);
    } else {
      socket = new WebSocket(
        `${window.location.origin.replace(/^http/, 'ws')}/admin/letter${mockEndpoint}`
      );
    }

    // Set the WebSocket on open connection and call the callback if provided
    socket.onopen = () => {
      if (token) {
        socket.send(JSON.stringify({ type: 'auth', token })); // Send the token as the first message
      }
      setWs(socket);
      if (callback) {
        callback(); // Send query only when WebSocket is open
      }
    };

    // Handle incoming messages
    socket.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'chatId':
          setChatData((prev) => ({
            ...prev,
            chatId: data.id!,
          }));
          getRecentHistory();
          break;
        case 'aiResponseChunk':
          const newTextChunk: string = data.text;
          setChatData((prev) => ({
            ...prev,
            botResponse: {
              ...prev.botResponse,
              chatText: prev.botResponse.chatText + newTextChunk,
            },
          }));
          break;
        case 'messageEnd':
          setChatData((prev) => ({
            ...prev,
            botResponse: { chatText: '', sources: [] },
            chatMessages: [
              ...prev.chatMessages,
              {
                role: 'system',
                text: prev.botResponse.chatText,
                sources: prev.botResponse.sources,
                showSources: true,
              },
            ],
          }));
          break;
        case 'noSources':
          setChatData((prev) => ({
            ...prev,
            botResponse: { ...prev.botResponse, sources: [] },
          }));
          break;
        case 'aiSources':
          if (data.sources) {
            try {
              const newSources = data.sources as Source[];
              setChatData((prev) => ({
                ...prev,
                botResponse: { ...prev.botResponse, sources: newSources },
              }));
            } catch (error) {
              // Optionally handle error
            }
          }
          break;
        case 'inputError':
          console.log(`an error occured with inputs`);
          // TODO prompt user that an error occured and to adjust inputs?
          break;
        case 'serverError':
          console.log(`an error occured with the server`);
          // TODO Tell user server had an issue and maybe try again later
          break;
        case 'authError':
          nav('/signin');
          break;
        default:
          console.error('Unknown message type:', data.type);
          console.log(data);
      }
    };

    socket.onerror = (error: Event) => {
      console.error('WebSocket error:', error);
    };

    // Cleanup WebSocket connection on component unmount
    return () => {
      socket.close();
    };
  };

  const sendQuery = () => {
    const model = 'deepseek-chat';
    if (ws && ws.readyState === WebSocket.OPEN) {
      const payload = {
        modelType: model,
        query: chatData.query,
        educationLevel: chatSettings.educationLevel,
        detailLevel: chatSettings.detailLevel,
        threshold: chatSettings.threshold,
        chatId: chatData.chatId === -1 ? null : chatData.chatId,
        selectedPackages: selectedPackages,
      };
      setChatData((prev) => ({
        ...prev,
        query: '',
        botResponse: { chatText: '', sources: [] },
        chatMessages: [
          ...prev.chatMessages,
          { role: 'user', text: chatData.query, sources: [] },
        ],
      }));
      ws.send(JSON.stringify(payload));
    }
  };

  const handleSubmit = () => {
    if (ws && ws.readyState === WebSocket.OPEN && chatData.query != '') {
      sendQuery();
    } else {
      startWebSocket(sendQuery); // Ensure WebSocket is open before sending the query
    }
    // sendQuery();
  };

  const getRecentHistory = async () => {
    const url = '/chat/history';
    setLoadingHistory(true);
    try {
      const jsonData = {
        maxHistorySize: 20,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(jsonData),
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }

        throw new Error(`Network response was not ok`);
      }
      const json = await response.json();
      const history = JSON.parse(json) as TrimmedHistory[];
      setLoadingHistory(false);
      setRecentHistory(history);
    } catch (error) {
      setLoadingHistory(true);
      setHistoryError(true);
    }
  };

  const handleHistoryClicked = async (historyId: number) => {
    try {
      const url = `/chat/history/specific?chatHistoryId=${historyId}&maxHistorySize=20`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });

      if (!response.ok) {
        throw Error(
          `There was an error with the history network call, ${response.status}`
        );
      }

      const json: { chatRole: string; chatText: string; sources?: Source[] }[] =
        await response.json();

      const chatMessages: ChatMessage[] = json.map((obj) => ({
        role: obj.chatRole,
        text: obj.chatText,
        sources: obj.sources || [],
        showSources: false, // Initialize showSources for history messages
      }));
      chatMessages.reverse();
      setChatData({
        query: '',
        botResponse: { chatText: '', sources: [] },
        chatMessages: chatMessages,
        chatId: historyId,
      });
    } catch (error) {
      console.log(`An error occurred:`);
      console.log(error);
    }
  };

  const handleNewChatClicked = async () => {
    setChatData({
      query: '',
      botResponse: { chatText: '', sources: [] },
      chatMessages: [],
      chatId: -1,
    });
    await getRecentHistory();
  };

  const handleSetSelectedPackage = async (item: string) => {
    // TODO, when setting up selecting multiple packages for real, this will need to be changed
    if (selectablePackages.includes(item)) {
      setSelectedPackages([item]);
    } else {
      // TODO remove and replace with an actual error notification for user, or omit it and handle in some other way.
      console.log(
        `Somehow an incorrect package was selected, are you doing something strange?`
      );
    }
  };

  const handleShowSources = (messageIndex: number) => {
    setChatData((prev) => ({
      ...prev,
      chatMessages: prev.chatMessages.map((msg, idx) =>
        idx === messageIndex ? { ...msg, showSources: !msg.showSources } : msg
      ),
    }));
  };

  // Get required background info
  useEffect(() => {
    getValidPackages();
    getRecentHistory();
  }, []);

  // Set up the WebSocket on component mount
  useEffect(() => {
    startWebSocket();
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  const handleHide = () => {
    setHide(!hide);
  };

  return (
    <SidePanelScaffold
      isLoggedIn={loginState.isLoggedIn}
      isAdmin={loginState.isAdmin}
      stickyTopBar={true}
      isExpanded={sideBarExpanded}
      showCollapseIcons={true}
      onExpandedToggle={toggleSideBarExpansion}
      userName={currentUserName}
      sideBarHeaderChildren={
        <div className="new-chatbot" onClick={handleNewChatClicked}>
          <span>
            New Chat <img src={editIcon} alt="Edit Icon" />
          </span>
        </div>
      }
      topBarChildren={
        <ChatTopBar
          isSticky={true}
          isLoggedIn={loginState.isLoggedIn}
          showAdminButton={loginState.isAdmin}
          selectablePackages={selectablePackages}
          selectedPackages={selectedPackages}
          onPackageSelect={handleSetSelectedPackage}
          userName={currentUserName}
          additionalRightChildren={
            <div className="gearIconDiv">
              <IoSettingsOutline
                className="gearIcon"
                onClick={() => {
                  handleHide();
                }}
              />
              <div className={`ChatPageSetting ${hide ? 'hidden' : ''}`}>
                <div>
                  <p>Education Level:</p>
                  <InputField
                    value={chatSettings.educationLevel}
                    label={'Education Level'}
                    onValueChange={handleEducationLevelChange}
                  ></InputField>
                </div>
                <div>
                  <p> Detail Amount: </p>
                  <DropDownMenu
                    choices={levels}
                    onSelect={(value) => handleDetailChange(value)}
                  ></DropDownMenu>
                </div>
                <div>
                  <p>Relevancy Threshold:</p>
                  <NumberInputField
                    value={chatSettings.threshold}
                    label={'Threshold'}
                    onValueChange={handleThresholdChange}
                  ></NumberInputField>
                </div>
              </div>
            </div>
          }
        />
      }
      sideBarChildren={
        <div>
          {/* We should probably move this out to be it's own component */}
          <div className="ChatListContainer">
            {loadingHistory ? (
              <LoadingSpinner />
            ) : (
              <ul className="chat-history">
                {recentHistory.map((chat) => (
                  <li
                    key={chat.chatHistoryId}
                    onClick={() => handleHistoryClicked(chat.chatHistoryId)}
                  >
                    <img src={editIcon} alt="Chat Icon" className="chat-icon" />{' '}
                    <span>{chat.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* TODO @Furqan, fix this, there is no show more shown. If you do not want it on the page, remove it */}
          {/* If we are keeping it, make it expand the amount of history that will show in trimmed history, and allow the history to then be scrollable */}
          <div className="show-more">
            <span
              onClick={() => {
                nav('/chat/history/search');
              }}
            >
              Show More
            </span>
          </div>
        </div>
      }
      mainPageChildren={
        <div className="main-chat-container">
          <div className="primaryChatContainer">
            <div className="ChatPageMainWrapper">
              <div className="ChatPageChatArea">
                {chatData.chatMessages.length === 0 &&
                !chatData.botResponse.chatText ? (
                  <div className="defaultScreen">
                    <img
                      src={'/video-search_Icon.png'}
                      alt="Logo"
                      className="logo"
                    />
                    <h4 className="defaultScreenName">Video Search AI</h4>
                  </div>
                ) : (
                  <div className="ChatScrollArea">
                    {chatData.chatMessages.map((msg, i) => (
                      <div key={i} className={`chat-message ${msg.role}`}>
                        {msg.role === 'system' ? (
                          <div
                            className={`system-message-layout ${!(msg.sources && msg.sources.length > 0 && msg.showSources) ? 'no-sources-shown' : ''}`}
                          >
                            {/* Sources on the left */}
                            {msg.sources &&
                              msg.sources.length > 0 &&
                              msg.showSources && (
                                <div className="message-sources-left">
                                  <div className="sources-container-left">
                                    {msg.sources.map((source, index) => (
                                      <SourceCard source={source} key={index} />
                                    ))}
                                  </div>
                                </div>
                              )}
                            {/* Message content on the right */}
                            <div className="message-content-with-button">
                              <div className="message-content-right">
                                {msg.text}
                                {msg.sources && msg.sources.length > 0 && (
                                  <div className="message-sources-bottom">
                                    <button
                                      className="show-sources-btn"
                                      onClick={() => handleShowSources(i)}
                                    >
                                      {msg.showSources
                                        ? 'Hide References'
                                        : 'Show References'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="message-content">{msg.text}</div>
                        )}
                      </div>
                    ))}
                    {chatData.botResponse.chatText != '' && (
                      <div className="chat-message system">
                        <div
                          className={`system-message-layout ${chatData.botResponse.sources.length === 0 ? 'no-sources-shown' : ''}`}
                        >
                          {/* Sources on the left */}
                          {chatData.botResponse.sources.length > 0 && (
                            <div className="message-sources-left">
                              <div className="sources-container-left">
                                {chatData.botResponse.sources.map(
                                  (source, index) => (
                                    <SourceCard source={source} key={index} />
                                  )
                                )}
                              </div>
                            </div>
                          )}
                          {/* Message content on the right */}
                          <div className="message-content-with-button">
                            <div className="message-content-right">
                              {chatData.botResponse.chatText}
                            </div>
                            {chatData.botResponse.sources.length > 0 && (
                              <div className="message-sources-bottom">
                                <span className="references-indicator">
                                  References loading...
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="ChatSearchContainer">
              <div className="ChatPageQuery">
                <div className="search_setting-icon">
                  <img src={settingsIcon} alt="Settings Icon" />
                </div>
                <ChatInputField
                  value={chatData.query}
                  label={'Write query here...'}
                  onValueChange={handleQueryChange}
                  onEnterPress={handleSubmit}
                ></ChatInputField>
                <IoMdSend
                  onClick={handleSubmit}
                  className="ChatPageConfirmButton"
                />
              </div>
            </div>
            <BugButton parentPageUrl={'/chat'} token={token} nav={nav} />
            <FeedBackButton parentPageUrl={'/chat'} token={token} nav={nav} />
          </div>
        </div>
      }
    ></SidePanelScaffold>
  );
}

export default LetterPage;
